import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import CustomModal from "../../../../components/Modals/CustomModal";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import { GREY, BRAND, BASE } from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import CircularLoader from "../../../../components/Loaders/CircularLoader";

const CampaignStatusModal = ({
  open,
  onClose,
  status,
  prospectCount = 250,
  onViewCampaign,
  logoSrc,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  const tips = [
    "Personalization increases email open rates by up to 26%. Always reference specific details about your prospect's company or role.",
    "The best time to send B2B emails is Tuesday through Thursday, between 10 AM and 2 PM in your prospect's timezone.",
    "Keep your subject lines under 50 characters. Emails with shorter subject lines have 12% higher open rates.",
    "A/B test your email sequences regularly. Even small changes in call-to-action wording can improve response rates by 20-30%",
  ];

  const steps = [
    "Analyzing prospect data",
    "Generating personalized emails",
    "Creating call scripts",
    "Setting up tasks and timeline",
  ];

  const isProcessing = status === "Ai Processing" || status === "processing";

  // Rotate tips every 8 seconds
  useEffect(() => {
    if (!isProcessing) return;

    const tipInterval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % tips.length);
    }, 8000);

    return () => clearInterval(tipInterval);
  }, [isProcessing]);

  // Simulate progress through steps for visual feedback if status is processing
  useEffect(() => {
    if (!isProcessing) return;

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= steps.length - 1) return prev;
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(stepInterval);
  }, [isProcessing]);

  if (!open || !isProcessing) return null;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      hideHeader={true}
      width="520px"
      remove_padding={true}
      blurBackdrop={true}
      BackdropProps={{
        style: {
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
      }}
    >
      <Box sx={styles.modalContent}>
        {isProcessing && (
          <Box sx={styles.processingView}>
            {/* AI Animation */}
            <Box sx={styles.animationContainer}>
              <Box sx={styles.aiPulse}>
                <Box
                  sx={{
                    ...styles.aiRing,
                    animation: "ringPulse 2s ease-out infinite",
                  }}
                />
                <Box
                  sx={{
                    ...styles.aiRing,
                    animation: "ringPulse 2s ease-out infinite",
                    animationDelay: "0.4s",
                  }}
                />
                <Box
                  sx={{
                    ...styles.aiRing,
                    animation: "ringPulse 2s ease-out infinite",
                    animationDelay: "0.8s",
                  }}
                />
                <Box sx={styles.aiCore} />
              </Box>
            </Box>

            {/* Header */}
            <Box sx={styles.header}>
              <TypographyInline
                label="Setting Up Your Campaign"
                size="xl"
                fontWeight={fontWeight.BOLD}
                color={GREY.PRIMARY}
                style={{
                  fontSize: "24px",
                  marginBottom: "8px",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  fontSize: "15px",
                  color: GREY.SECONDARY,
                  lineHeight: 1.5,
                }}
              >
                We're personalizing content for{" "}
                <strong style={{ color: BRAND.PRIMARY }}>
                  {prospectCount} prospects
                </strong>{" "}
                across multiple channels. This may take a few minutes.
              </Box>
            </Box>

            {/* Progress Steps */}
            <Box sx={styles.progressSection}>
              <Box sx={styles.progressSteps}>
                {steps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      ...styles.progressStep,
                      ...(index < currentStep
                        ? styles.progressStepCompleted
                        : {}),
                      ...(index === currentStep
                        ? styles.progressStepActive
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        ...styles.stepIcon,
                        ...(index < currentStep
                          ? styles.stepIconCompleted
                          : {}),
                        ...(index === currentStep ? styles.stepIconActive : {}),
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box
                      sx={{
                        ...styles.stepText,
                        ...(index === currentStep ? styles.stepTextActive : {}),
                      }}
                    >
                      {step}
                    </Box>
                    {index === currentStep && (
                      <Box sx={styles.spinnerWrapper}>
                        <CircularLoader size={20} loadercolor={BRAND.PRIMARY} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Tips Section */}
            <Box sx={styles.tipsSection}>
              <Box sx={styles.tipsHeader}>
                <Box sx={styles.tipsIcon}>💡</Box>
                <TypographyInline
                  label="PRO TIP"
                  size="xs"
                  fontWeight={fontWeight.BOLD}
                  color={GREY.SECONDARY}
                  style={{ letterSpacing: "0.5px" }}
                />
              </Box>
              <Box key={currentTipIndex} sx={styles.tipContent}>
                <TypographyInline
                  label={tips[currentTipIndex]}
                  size="sm"
                  color={GREY.PRIMARY}
                  style={{ lineHeight: 1.6 }}
                />
              </Box>
              <Box sx={styles.tipDots}>
                {tips.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      ...styles.tipDot,
                      ...(index === currentTipIndex ? styles.tipDotActive : {}),
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={styles.actions}>
              <Button
                label="Go Back"
                variant="ghost"
                size="sm36"
                onClick={onClose}
                fullWidth
              />
            </Box>

            {/* Footer Logo */}
            <Box sx={styles.footerLogoContainer}>
              <img src={logoSrc} alt="logo" style={styles.footerLogo} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Inject animations */}
      <style>{`
        @keyframes corePulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.9;
          }
        }

        @keyframes ringPulse {
          0% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(0.8);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.4);
          }
        }
      `}</style>
    </CustomModal>
  );
};

const styles = {
  modalContent: {
    padding: "40px",
    backgroundColor: BASE.WHITE,
    borderRadius: "16px",
    overflow: "hidden",
  },
  processingView: {
    display: "flex",
    flexDirection: "column",
  },
  animationContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "32px",
  },
  aiPulse: {
    position: "relative",
    width: "80px",
    height: "80px",
  },
  aiCore: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "40px",
    height: "40px",
    background: BRAND.PRIMARY,
    borderRadius: "50%",
    animation: "corePulse 2s ease-in-out infinite",
  },
  aiRing: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    border: `2px solid ${BRAND.PRIMARY}`,
  },
  header: {
    textAlign: "center",
    marginBottom: "24px",
  },
  progressSection: {
    marginBottom: "32px",
  },
  progressSteps: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  progressStep: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    background: "rgba(255, 72, 72, 0.05)",
    opacity: 0.4,
    transition: "all 0.3s ease",
  },
  progressStepActive: {
    opacity: 1,
    background: "rgba(255, 72, 72, 0.1)",
  },
  progressStepCompleted: {
    opacity: 0.6,
    background: "#f5f5f5",
  },
  stepIcon: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    flexShrink: 0,
    background: "#e0e0e0",
    color: "#999",
  },
  stepIconActive: {
    background: BRAND.PRIMARY,
    color: "white",
  },
  stepIconCompleted: {
    background: GREY.SECONDARY,
    color: "white",
  },
  stepText: {
    fontSize: "14px",
    color: GREY.PRIMARY,
    fontWeight: 500,
  },
  stepTextActive: {
    fontWeight: 600,
  },
  spinnerWrapper: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },
  tipsSection: {
    background: "#f9f9f9",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
  },
  tipsHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  tipsIcon: {
    width: "20px",
    height: "20px",
    background: BRAND.PRIMARY,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "12px",
  },
  tipDots: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
    marginTop: "16px",
  },
  tipDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#d0d0d0",
    transition: "all 0.3s ease",
  },
  tipDotActive: {
    background: BRAND.PRIMARY,
    width: "20px",
    borderRadius: "3px",
  },
  actions: {
    display: "flex",
    gap: "12px",
    width: "100%",
  },
  footerLogoContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
    width: "100%",
  },
  footerLogo: {
    height: "18px",
    opacity: 0.6,
  },
};

export default CampaignStatusModal;
